import React from "react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  CssBaseline,
  Backdrop,
  CircularProgress,
} from "@mui/material";

import {
  // AreaAlbumsTableHeadCells,
  drawerWidth,
  mainLayoutStyles,
  routes,
  showNotification,
} from "../../../../constants";
import Sidebar from "../../../../common/Sidebar";
import DetailsPageHeader from "../../../../common/DetailsPageHeader";

import { AreaDetailsDataFieldsSection } from "../../components/components";

import { useAlbumsContext } from "../../../../providers/OrganizationAdmin/AlbumProvider";
import {
  getAreabyId,
  updateArea,
  getAreabyPosition,
  normalizeQuickCollectSlug,
} from "../../hooks/AreaHook";
import {
  // albumsSearch,
  quickTalesSearch,
} from "../../../OrganizationAlbums/hooks/AlbumsHooks";
import { useAreasContext } from "../../../../providers/OrganizationAdmin/AreaProvider";
import { Toast } from "../../../../common/alert";
import { getSingleUser } from "../../../OrganizationQuickTales/hooks/QuickTalesHooks";
import CommonHeader from "../../../../common/CommonHeader";
import TableSearchBar from "../../../../common/TableSearchBar";
import CommonTable from "../../../../common/CommonTable";
import QuickTalesTableRow from "../../../../common/QuickTalesTableRow";
import { useAuth } from "../../../../hooks";

const AreaDetailsContainer = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { allAreas, setAllAreas, handleGetAllAreas, loading, setLoading } =
    useAreasContext();
  const { handleQuicktalesData, handleGetAreaAlbumsData } = useAlbumsContext();

  // const [areaDetails, setAreaDetails] = useState(location.state.areaDetails);
  const [areaDetails, setAreaDetails] = useState(location.state.areaDetails);
  const [editEnabled, setEditEnabled] = useState(false);
  const [selectedQuickTales, setSelectedQuickTales] = useState([]);
  // const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [selectedQuicktalesNAlbums, setSelectedQuicktalesNAlbums] = useState(
    []
  );
  const [quicktaleAndAlbumsData, setQuicktaleAndAlbumsData] = useState([]);
  const [quickDetail, setQuickDetail] = useState([]);
  const [albumsData, setAlbumsData] = useState([]);
  const [tabValue, setTabValue] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    fetchAreaDetails();
  }, [location.state.areaDetails]);

  // Render loading state or actual details based on areaDetails
  if (!areaDetails) {
    return <div>Loading...</div>;
  }

  const fetchAreaDetails = async () => {
    try {
      setTableLoading(true);
      const areaId = location.state.areaDetails.id;
      const areaData = await getAreabyId(areaId);
      const albumsAndQTs = areaData.data().albumsAndQTs;

      // Prepare an array to store the final ordered data
      let orderedData = [];

      // Fetch information for each item in albumsAndQTs in the order given
      for (let element of albumsAndQTs) {
        if (element.type === "albums") {
          const albumInfo = await handleGetAreaAlbumsData([element.id]);
          if (albumInfo && albumInfo.length > 0) {
            albumInfo.forEach((albumData) => {
              orderedData.push({
                ...albumData,
                position: element?.position,
                type: element?.type,
              });
            });
            // orderedData.push(...albumInfo);
          }
        } else if (element.type === "quicktales") {
          const quicktaleInfo = await handleQuicktalesData([element.id]);
          if (quicktaleInfo && quicktaleInfo.quicktaleData.length > 0) {
            quicktaleInfo.quicktaleData.forEach((qtsItems) => {
              // Only display quicktales that are (public OR unlisted) AND approved.
              const listenStatus = (qtsItems?.status || "").toLowerCase();
              const approvalStatus = (
                qtsItems?.approvalStatus?.status || ""
              ).toLowerCase();
              const isListenable =
                listenStatus === "public" || listenStatus === "unlisted";
              const isApproved = approvalStatus === "approved";
              if (!isListenable || !isApproved) return;

              orderedData.push({
                ...qtsItems,
                position: element?.position,
                type: element?.type,
              });
            });
            // orderedData.push(...quicktaleInfo.quicktaleData);
          }
        }
      }

      // Fetch user data
      let userTemp = await getSingleUser(areaData?.data()?.user);

      // Prepare the final data object
      let data = {
        ...areaData.data(),
        id: areaData.id,
        userData: userTemp?.data() ? userTemp?.data() : {},
        quicktaleAndAlbumsData: orderedData,
        quicktaleData: orderedData.filter((item) => item.type === "quicktales"),
        albumsData: orderedData.filter((item) => item.type === "albums"),
        audioLength: orderedData
          .filter((item) => item.type === "quicktales")
          .reduce((total, item) => total + (item.audioLength || 0), 0),
      };

      // Set the fetched data in the state
      setAreaDetails(data);
      setQuicktaleAndAlbumsData(data.quicktaleAndAlbumsData);
      setQuickDetail(data.quicktaleData);
      setAlbumsData(data.albumsData);

      console.log("data", data);
    } catch (error) {
      console.error("Error fetching area details:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleUpdateAreaData = async () => {
    //check if position is a number
    if (isNaN(areaDetails.position)) {
      Toast("error", "Position should be a number.");
      return;
    }
    //next position
    let nextPosition = allAreas.length + 1;
    if (areaDetails.position > nextPosition) {
      Toast(
        "error",
        "Position should be less than or equal to " + nextPosition
      );
      return;
    }
    console.log("old position", location.state.areaDetails.position);
    let position = parseInt(areaDetails.position);
    let positionExist = await getAreabyPosition(position);
    if (positionExist.length > 0 && positionExist[0].id != areaDetails.id) {
      //update area position
      await updateArea(positionExist[0].id, {
        position: parseInt(location.state.areaDetails.position),
      });
      // Toast("error", "Position already taken.");
      // return;
    }

    try {
      setLoading(true);
      // Prepare new area object
      const newAreaData = {
        name: areaDetails?.name ?? "",
        isActive: areaDetails?.isActive == "Active" ? true : false,
        position: position,
        updatedAt: new Date(),
        updatedBy: currentUser?.id ?? "",
        // Keep the QuickCollect slug in sync when the area is renamed, so the
        // public link always matches the current name.
        // See docs/QUICKCOLLECT_IMPLEMENTATION.md
        quickCollectSlug: normalizeQuickCollectSlug(areaDetails?.name ?? ""),
      };

      let responseTemp = await updateArea(areaDetails.id, newAreaData);
      showNotification("Area updated successfully.");
      // Toast("success", "Area updated successfully.");

      await handleGetAllAreas();
    } catch (error) {
      console.error("Error submitting area data:", error);
    } finally {
      setLoading(false);
      setEditEnabled(false);
    }
  };

  const handleSearchQuickTalesNAlbums = async (e) => {
    try {
      let value = e.target.value;

      if (value.length >= 1) {
        let quicktaleSearchTemp = await quickTalesSearch(
          areaDetails?.quicktaleAndAlbumsData,
          value
        );
        setQuicktaleAndAlbumsData(quicktaleSearchTemp);
      } else {
        setQuicktaleAndAlbumsData(areaDetails.quicktaleAndAlbumsData);
      }
    } catch (error) {
      console.log("error searching album and quicktales:", error.message);
    }
  };

  const handleRemoveQuickTale = async (selectedQuicktales) => {
    try {
      let quicktales = areaDetails.albumsAndQTs;
      // let quicktales = areaDetails.quicktales;
      // Filter out the selectedQuicktales from the current quicktales
      const updatedQuicktales = quicktales.filter(
        (qt) => !selectedQuicktales.includes(qt.id)
      );
      let dataTemp = {
        albumsAndQTs: updatedQuicktales,
        // quicktales: updatedQuicktales,
        updatedAt: new Date(),
        updatedBy: currentUser?.id,
      };
      let responseTemp = await updateArea(areaDetails.id, dataTemp);
      // setAreaDetails((prevDetails) => ({
      //   ...prevDetails,
      //   albumsAndQTs: updatedQuicktales,
      // }));
      // setQuickDetail((prevList) =>
      //   prevList.filter((item) => !selectedQuicktales.includes(item.id))
      // );
      await fetchAreaDetails();
      await handleGetAllAreas();
      setSelectedQuickTales([]);
      setSelectedQuicktalesNAlbums([]);
      showNotification("Area quicktales/albums removed successfully.");
      // Toast("success", "Area quicktales removed successfully.");
    } catch (error) {
      console.log("error removing area quicktales:", error.message);
    }

    // setQuickDetail(selected);
  };

  // const handleSearchAreaQuickTales = async (e) => {
  //   try {
  //     let value = e.target.value;

  //     if (value.length >= 1) {
  //       let quicktaleSearchTemp = await quickTalesSearch(
  //         areaDetails?.quicktaleData,
  //         value
  //       );
  //       setQuickDetail(quicktaleSearchTemp);
  //     } else {
  //       setQuickDetail(areaDetails.quicktaleData);
  //     }
  //   } catch (error) {
  //     console.log("error searching album:", error.message);
  //   }
  // };
  // const handleSearchAreaAlbums = async (e) => {
  //   try {
  //     let value = e.target.value;

  //     if (value.length >= 1) {
  //       let albumsSearchTemp = await albumsSearch(
  //         areaDetails?.albumsData,
  //         value
  //       );
  //       setAlbumsData(albumsSearchTemp);
  //     } else {
  //       setAlbumsData(areaDetails.albumsData);
  //     }
  //   } catch (error) {
  //     console.log("error searching album:", error.message);
  //   }
  // };

  // const handleRemoveAlbums = async (selectedAlbums) => {
  //   try {
  //     let albums = areaDetails?.albums;
  //     // Filter out the selectedAlbums from the current albums
  //     const updatedAlbums = albums.filter(
  //       (albumId) => !selectedAlbums.includes(albumId)
  //     );
  //     let dataTemp = {
  //       albums: updatedAlbums,
  //       updatedAt: new Date(),
  //       updatedBy: currentUser?.id,
  //     };
  //     let responseTemp = await updateArea(areaDetails.id, dataTemp);
  //     setAreaDetails((prevDetails) => ({
  //       ...prevDetails,
  //       albums: updatedAlbums,
  //     }));
  //     setAlbumsData((prevList) =>
  //       prevList.filter((item) => !selectedAlbums.includes(item.id))
  //     );
  //     await handleGetAllAreas();
  //     setSelectedAlbums([]);
  //     showNotification("Area albums removed successfully.");
  //     // Toast("success", "Area albums removed successfully.");
  //   } catch (error) {
  //     console.log("error removing area albums:", error.message);
  //   }

  //   // setQuickDetail(selected);
  // };

  // const isGeneralModeratorOrStandard =
  //   currentUser?.type === "general" &&
  //   (currentUser?.role === "moderator" || currentUser?.role === "standard");

  return (
    <Box sx={{ display: "flex", width: `calc(100% - ${drawerWidth}px)` }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`'${
          areaDetails && areaDetails.name && areaDetails.name
        }' Area`}
        bgColor="#fff"
        buttonText={
          currentUser?.role === "super" || currentUser?.role === "moderator"
            ? "Edit"
            : undefined
        }
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled ? true : false}
        // handleSave={() => setEditEnabled(false)}
        handleSave={() => {
          handleUpdateAreaData();
        }}
        loading={loading}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          {/*Data card/container section*/}
          <AreaDetailsDataFieldsSection
            areaDetails={areaDetails}
            setAreaDetails={setAreaDetails}
            editEnabled={editEnabled}
          />
          <CommonHeader
            mainTitle={`QuickTales/Albums in '${
              areaDetails && areaDetails.name && areaDetails.name
            }'`}
            bgColor="#fff"
            buttonText={"Add QuickTales"}
            SecondButtonText={"Add Albums"}
            // buttonText={
            //   isGeneralModeratorOrStandard ? undefined : "Add QuickTales"
            // }
            handleSubmit={() => {
              navigate(routes.addQTToArea, {
                state: { areaDetails },
              });
            }}
            handleSecondButtonSubmit={() => {
              navigate(routes.addAlbumsToArea, {
                state: { areaDetails },
              });
            }}
          />
          <TableSearchBar
            selected={selectedQuicktalesNAlbums}
            setSelected={setSelectedQuicktalesNAlbums}
            placeholder={"Search"}
            handleRemove={handleRemoveQuickTale}
            handleSearch={handleSearchQuickTalesNAlbums}
            // selected={tabValue === 1 ? selectedQuickTales : selectedAlbums}
            // setSelected={
            //   tabValue === 1 ? setSelectedQuickTales : setSelectedAlbums
            // }
            // handleSearch={
            //   tabValue === 1
            //     ? handleSearchAreaQuickTales
            //     : handleSearchAreaAlbums
            // }
            // handleRemove={
            //   tabValue === 1 ? handleRemoveQuickTale : handleRemoveAlbums
            // }
          />

          <CommonTable
            moduleDetails={areaDetails}
            module="area"
            // tableData={albumsData}
            tableData={quicktaleAndAlbumsData}
            setTableData={setQuicktaleAndAlbumsData}
            selected={selectedQuicktalesNAlbums}
            setSelected={setSelectedQuicktalesNAlbums}
            CommonTableRow={QuickTalesTableRow}
            removeRow={handleRemoveQuickTale}
            showPosition={true}
            hideRemoveLink={true}
            onTitleClick={(row) => {
              // Only quicktales are clickable to open the QuickTale details page.
              if (row?.type !== "quicktales") return;
              navigate(routes.quicktaleInOrgDetails.replace(":quicktaleId", row.id), {
                state: { quicktaleDetails: row },
              });
            }}
            handleSavePosition={async (quicktalesData) => {
              try {
                let tempData = [];
                let tempTableData = [];
                quicktalesData.forEach((element, index) => {
                  tempTableData.push({
                    id: element.id,
                    type: element.type,
                    position: index + 1,
                    ...element,
                  });
                  tempData.push({
                    id: element.id,
                    type: element.type,
                    position: index + 1,
                  });
                });
                let temp = {
                  albumsAndQTs: tempData,
                };
                setQuicktaleAndAlbumsData(tempTableData);
                console.log("temp data:::", tempData);
                await updateArea(areaDetails.id, temp);
              } catch (error) {
                console.log("error re-ordering qts:", error.message);
              }
            }}
          />

          {/* <Box sx={{ width: "100%", typography: "body1" }}>
            <TabContext value={tabValue}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                  onChange={handleChangeTab}
                  aria-label="lab API tabs example"
                >
                  <Tab
                    label="Area QuickTales"
                    style={{ textTransform: "none" }}
                    value={1}
                  />
                  <Tab
                    label="Area Albums"
                    style={{ textTransform: "none" }}
                    value={2}
                  />
                </TabList>
              </Box>
              <TabPanel value={1}>
                <CommonTable
                  moduleDetails={areaDetails}
                  module="area"
                  // tableData={albumsData}
                  tableData={quickDetail}
                  setTableData={setQuickDetail}
                  selected={selectedQuickTales}
                  setSelected={setSelectedQuickTales}
                  CommonTableRow={QuickTalesTableRow}
                  removeRow={handleRemoveQuickTale}
                  showPosition={true}
                  handleSavePosition={async (quicktalesData) => {
                    try {
                      let temp = {
                        quicktales: quicktalesData,
                      };
                      // console.log("temp data:::", temp)
                      // console.log("id data:::", albumDetails.id)
                      await updateArea(areaDetails.id, temp);
                    } catch (error) {
                      console.log("error re-ordering qts:", error.message);
                    }
                  }}
                />
              </TabPanel>
              <TabPanel value={2}>
                <CommonTable
                  moduleDetails={areaDetails}
                  module="area"
                  // tableData={albumsData}
                  tableData={albumsData}
                  setTableData={setAlbumsData}
                  selected={selectedAlbums}
                  setSelected={setSelectedAlbums}
                  CommonTableRow={AreaAlbumsTableRow}
                  removeRow={handleRemoveAlbums}
                  showPosition={true}
                  tableHeadData={AreaAlbumsTableHeadCells}
                  albumsTable={true}
                  handleSavePosition={async (albumsData) => {
                    try {
                      let temp = {
                        albums: albumsData,
                      };
                      // console.log("temp data:::", temp)
                      // console.log("id data:::", areaDetails.id)
                      await updateArea(areaDetails.id, temp);
                    } catch (error) {
                      console.log("error re-ordering qts:", error.message);
                    }
                  }}
                />
              </TabPanel>
            </TabContext>
          </Box> */}
          {/* <CommonTable
            moduleDetails={areaDetails}
            module="area"
            // tableData={albumsData}
            tableData={[...quickDetail, ...albumsData]}
            setTableData={setQuickDetail}
            setQuickDetail={setQuickDetail}
            selected={selectedQuickTales}
            setSelected={setSelectedQuickTales}
            CommonTableRow={QuickTalesTableRow}
            removeRow={handleRemoveQuickTale}
          /> */}
        </Grid>
      </Grid>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={tableLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default AreaDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
